import { FC, Fragment, HTMLAttributes, useEffect, useState } from "react";
import Input from "shared/Input/Input";
import Textarea from "shared/Textarea/Textarea";
import FormItem from "./FormItem";

import { zodResolver } from "@hookform/resolvers/zod";
import { Alert } from "@material-tailwind/react";
import { ServiceFormProps } from "data/reduxInterfaces";
import { ProfilService } from "data/types";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import Multiselect from "multiselect-react-dropdown";
import {
  PencilIcon,
  MapPinIcon,
  DocumentTextIcon,
  SparklesIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { motion } from "framer-motion";

interface SelectedValue {
  label: string;
}

const ServiceDetails: FC<ServiceFormProps & HTMLAttributes<HTMLDivElement>> = ({
  className,
  isLoading,
  handleForm,
  errorServiceForm,
  FormData,
}) => {
  const serviceState = useAppSelector((state) => state.service);

  const [defaultRegion, setDefaultRegion] = useState<string[]>([]);

  const formSchema = z.object({
    title: z
      .string()
      .min(1, { message: "Le nom du service est obligatoire." })
      .max(100, { message: "Le nom ne peut pas dépasser 100 caractères." }),
    region: z.array(z.string()).min(1, { message: "Veuillez sélectionner au moins une région d'intervention." }),
    description: z
      .string()
      .min(10, { message: "La description doit contenir au moins 10 caractères." })
      .max(1000, { message: "La description ne peut pas dépasser 1000 caractères." }),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {},
    mode: "onSubmit",
  });

  const authState = useAppSelector((state) => state.auth);
  const availability = useAppSelector((state) => {
    const profile = state.auth.user?.profil;

    if (profile && "availability" in profile) {
      return profile.availability;
    }
    return [];
  });

  // Debug: Affichage console pour vérifier les données
  useEffect(() => {
    console.log("Debug availability data:", availability);
    console.log("Availability length:", availability?.length);
    console.log("Full availability:", JSON.stringify(availability, null, 2));
    console.log("Debug auth state:", authState);
    console.log("Debug user:", authState.user);
    console.log("Debug token exists:", localStorage.getItem("token") ? "Yes" : "No");
  }, [availability, authState]);

  // Fallback avec données de test si availability est vide
  const availabilityOptions =
    availability?.length > 0
      ? availability
      : [
          "Paris",
          "Lyon",
          "Marseille",
          "Toulouse",
          "Nice",
          "Nantes",
          "Strasbourg",
          "Montpellier",
          "Bordeaux",
          "Lille",
          "Rennes",
          "Reims",
        ];

  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = (data) => {
    FormData(data);
  };

  useEffect(() => {
    if (handleForm.type === "chekServiceFormOne") {
      handleSubmit(onSubmit)();
    }
  }, [handleForm.type]);

  useEffect(() => {
    if (serviceState && serviceState?.serviceData && typeof serviceState.serviceData.region === "string") {
      setDefaultRegion([serviceState.serviceData.region]);
    } else if (serviceState && serviceState?.serviceData && Array.isArray(serviceState.serviceData.region)) {
      setDefaultRegion(serviceState?.serviceData?.region);
    }
  }, [serviceState]);

  const selectedValues: string[] = defaultRegion;
  const selectedLabels: SelectedValue[] = selectedValues?.map((value: string) => ({ label: value }));

  return (
    <Fragment>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        {/* Header moderne avec icône */}
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-3 bg-eventToBee/10 rounded-xl">
            <SparklesIcon className="w-6 h-6 text-eventToBee" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Informations générales</h2>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Définissez les informations de base de votre prestation
            </p>
          </div>
        </div>

        {/* Alert d'erreur moderne */}
        {errorServiceForm && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6"
          >
            <div className="flex items-center space-x-3">
              <InformationCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-red-800 text-sm font-medium">{errorServiceForm}</p>
            </div>
          </motion.div>
        )}

        <form className="space-y-8" onSubmit={handleSubmit(onSubmit)}>
          {/* Nom de la prestation avec icône */}
          <div className="space-y-3">
            <label className="flex items-center space-x-2 text-lg font-semibold text-gray-900 dark:text-white">
              <PencilIcon className="w-5 h-5 text-eventToBee" />
              <span>Nom de votre prestation *</span>
            </label>
            <div className="relative">
              <Input
                type="text"
                defaultValue={serviceState?.serviceData?.title}
                placeholder="Ex: Photographie de mariage, DJ pour événements..."
                disabled={isLoading}
                className="pl-4 pr-4 py-4 text-base border-2 border-gray-200 rounded-xl focus:border-eventToBee focus:ring-2 focus:ring-eventToBee/20 transition-all duration-200"
                {...register("title")}
              />
              {errors.title && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-red-500 text-sm mt-2 flex items-center space-x-1"
                >
                  <InformationCircleIcon className="w-4 h-4" />
                  <span>{errors.title.message}</span>
                </motion.p>
              )}
            </div>
          </div>

          {/* Régions disponibles avec icône */}
          <div className="space-y-3">
            <label className="flex items-center space-x-2 text-lg font-semibold text-gray-900 dark:text-white">
              <MapPinIcon className="w-5 h-5 text-eventToBee" />
              <span>Régions disponibles *</span>
            </label>
            <div className="relative">
              <Multiselect
                options={availabilityOptions.map((item: string) => ({ name: item, id: item }))}
                selectedValues={selectedLabels.map((item) => ({ name: item.label, id: item.label }))}
                onSelect={(selectedList: any[]) => {
                  const regions = selectedList.map((item) => item.name);
                  setDefaultRegion(regions);
                  setValue("region", regions.length > 0 ? (regions as [string, ...string[]]) : []);
                }}
                onRemove={(selectedList: any[]) => {
                  const regions = selectedList.map((item) => item.name);
                  setDefaultRegion(regions);
                  setValue("region", regions.length > 0 ? (regions as [string, ...string[]]) : []);
                }}
                displayValue="name"
                placeholder="Tapez pour rechercher des régions (ex: Par pour Paris)..."
                disable={isLoading}
                showCheckbox={false}
                selectionLimit={-1}
                onKeyPressFn={(e: any) => {
                  console.log("Key pressed:", e.key);
                }}
                style={{
                  chips: {
                    background: "#4663AC",
                    borderRadius: "8px",
                    margin: "3px",
                    padding: "8px 12px",
                    color: "white",
                    fontSize: "14px",
                    fontWeight: "500",
                  },
                  searchBox: {
                    border: "2px solid #E5E7EB",
                    borderRadius: "12px",
                    fontSize: "16px",
                    padding: "12px 16px",
                    minHeight: "auto",
                    focus: {
                      borderColor: "#4663AC",
                    },
                  },
                  inputField: {
                    margin: "0",
                    padding: "4px 0",
                    fontSize: "16px",
                    color: "#374151",
                    backgroundColor: "transparent",
                  },
                  option: {
                    background: "white",
                    color: "#374151",
                    padding: "12px 16px",
                    borderBottom: "1px solid #f3f4f6",
                    cursor: "pointer",
                  },
                  optionContainer: {
                    border: "1px solid #E5E7EB",
                    borderRadius: "12px",
                    marginTop: "8px",
                    maxHeight: "200px",
                    overflowY: "auto",
                    boxShadow: "0 10px 25px -3px rgba(0, 0, 0, 0.1)",
                    backgroundColor: "white",
                    zIndex: 1000,
                  },
                  highlightOption: {
                    background: "#4663AC",
                    color: "white",
                  },
                }}
              />
              {errors.region && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-red-500 text-sm mt-2 flex items-center space-x-1"
                >
                  <InformationCircleIcon className="w-4 h-4" />
                  <span>{errors.region.message}</span>
                </motion.p>
              )}
            </div>
          </div>

          {/* Description avec icône */}
          <div className="space-y-3">
            <label className="flex items-center space-x-2 text-lg font-semibold text-gray-900 dark:text-white">
              <DocumentTextIcon className="w-5 h-5 text-eventToBee" />
              <span>Description de votre prestation *</span>
            </label>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Décrivez en détail ce que vous proposez, votre expertise et ce qui vous rend unique
            </p>
            <div className="relative">
              <Textarea
                placeholder="Décrivez en détail votre prestation : ce que vous proposez, votre expertise, ce qui vous rend unique..."
                defaultValue={serviceState?.serviceData?.description}
                disabled={isLoading}
                rows={6}
                className="border-2 border-gray-200 rounded-xl focus:border-eventToBee focus:ring-2 focus:ring-eventToBee/20 transition-all duration-200 resize-none"
                {...register("description")}
              />
              <div className="absolute bottom-3 right-3 text-xs text-gray-400">Minimum 10 caractères</div>
              {errors.description && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-red-500 text-sm mt-2 flex items-center space-x-1"
                >
                  <InformationCircleIcon className="w-4 h-4" />
                  <span>{errors.description.message}</span>
                </motion.p>
              )}
            </div>
          </div>
        </form>

        {/* Conseils d'aide */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-4"
        >
          <div className="flex items-start space-x-3">
            <InformationCircleIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-blue-900 mb-2">Conseils pour une prestation réussie</h4>
              <ul className="text-blue-800 text-sm space-y-1">
                <li>• Choisissez un nom clair et mémorable pour votre prestation</li>
                <li>• Sélectionnez toutes les régions où vous pouvez intervenir</li>
                <li>• Rédigez une description détaillée qui met en valeur votre expertise</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </Fragment>
  );
};

export default ServiceDetails;
